import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { money, useTinycmsPlans } from '../account/tinycmsPlans';
import { MoreColumn, moreColumns } from '../nav/planNav';
import { useAddons } from './addons';

/**
 * More (non-Pro; "Plan-based top nav and the More tab", Prototype › Pro): what
 * upgrading adds. Pro never sees it: Campaigns, Social and Prism AI are in its
 * nav. Set REACT_APP_PRISM_LAUNCHED=false to leave Prism AI out.
 */
const PRISM_LAUNCHED = process.env.REACT_APP_PRISM_LAUNCHED !== 'false';

const COLUMNS: Record<
  MoreColumn,
  { eyebrow: string; tag: string; headline: string; body: string; items: string[] }
> = {
  analytics: {
    eyebrow: 'Website analytics',
    tag: 'Business and Pro',
    headline: 'See where your visitors come from.',
    body: 'Every page and article ranked by visits, from your own Google Analytics. This week’s visits stay on Today.',
    items: [
      'Traffic sources, including search and social',
      'Trends over time',
      'Every page and article ranked by visits',
    ],
  },
  campaigns: {
    eyebrow: 'Campaigns',
    tag: 'Pro',
    headline: 'Run a campaign from one place.',
    body: 'Group posts, a landing page and a sign-up form around one goal, and see what each brings in.',
    items: [
      'A landing page and sign-up form for each campaign',
      'Posts across every channel, scheduled together',
      'Invite people to work on just one campaign',
    ],
  },
  social: {
    eyebrow: 'Social media management',
    tag: 'Pro',
    headline: 'Every article, shared for you.',
    body: 'Your agent drafts posts from what you publish. Nothing goes out until you approve it.',
    items: [
      'Facebook, X, Bluesky, Instagram and LinkedIn',
      'One calendar to approve or skip every post',
      'Impressions, clicks and top posts for each channel',
    ],
  },
  prism: {
    eyebrow: 'Prism AI',
    tag: 'Pro',
    headline: 'Make your archive work twice.',
    body: 'It breaks each article into verified facts and the people, teams and places in it, so LLMs can cite you.',
    items: [
      'Research: ask your archive and get a cited answer',
      'Write: new drafts built only from what you’ve published',
    ],
  },
};

export function MoreScreen() {
  const navigate = useNavigate();
  const { selectedAccount } = useAccount();
  const { reachOn, fullAnalytics, plan, isOwner, ownerEmail } = useAddons();
  const { plans } = useTinycmsPlans();
  // Until the site has loaded, reachOn is true for everyone: hold off deciding.
  if (!selectedAccount) return null;
  if (reachOn) return <Navigate to="/today" replace />;

  const yearly = (key: string) => {
    const found = plans.find((p) => p.key === key);
    return found ? money(found.yearlyPrice) : null;
  };
  const planLabel = plans.find((p) => p.key === plan)?.label;
  const business = yearly('business');
  const pro = yearly('pro');
  const price = fullAnalytics
    ? pro && `${pro} a month, billed yearly`
    : business && pro && `Business ${business} · Pro ${pro} a month, billed yearly`;

  return (
    <main className="ink-body" data-screen-label="More">
      <h1 className="ink-h1">More</h1>
      <p className="more-sub">
        Take what you publish further.{' '}
        {selectedAccount?.name && planLabel
          ? `${selectedAccount.name} is on ${planLabel}, and everything you use today stays the same.`
          : 'Everything you use today stays the same.'}
      </p>
      <div className="more-columns">
        {moreColumns(fullAnalytics, PRISM_LAUNCHED).map((key) => {
          const column = COLUMNS[key];
          return (
            <section className="more-column" key={key}>
              <div className="more-eyebrow">
                {column.eyebrow} · {column.tag}
              </div>
              <h2 className="more-headline">{column.headline}</h2>
              <p className="more-body">{column.body}</p>
              <ul className="more-items">
                {column.items.map((item) => (
                  <li key={item}>
                    <span className="more-check" aria-hidden="true">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      <div className="more-foot">
        {isOwner ? (
          <div className="more-cta">
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => navigate('/settings/billing')}
            >
              {fullAnalytics ? 'Upgrade to Pro' : 'Compare plans'}
            </button>
            {price ? <span className="more-price">{price}</span> : null}
          </div>
        ) : (
          <p className="more-owner">
            Only the site owner{ownerEmail ? `, ${ownerEmail},` : ''} can change the plan.
          </p>
        )}
      </div>
    </main>
  );
}
